import { useAtomValue } from 'jotai'
import { useEffect, useMemo, useState } from 'react'

import _debounce from 'lodash/debounce'
import { cn } from '@/utils'

import { rundownAtom } from '@/store/rundown.store.ts'

import * as OUTPUT_TYPES from './rundown-output.const.ts'

import { useRundownOutputOptions } from './useRundownOutputOptions.ts'

import { RundownOutputHeader } from './RundownOutputHeader.tsx'
import { RundownOutputBody } from './RundownOutputBody.tsx'
import { RundownOutputControls } from './RundownOutputControls.js'

export function RundownOutput() {
  const [controlsVisible, setControlsVisible] = useState(false)

  const debouncedSetControlsVisible = useMemo(
    () => _debounce((value) => setControlsVisible(value), 2000),
    [],
  )

  function onMouseMove() {
    // Keep track of the active element so that we can ensure the controls only hide when we want them to.
    // If the user's mouse is hovering over - or they are interacting with - an element (e.g. the prompter controls) we want to ensure that the controls stay visible, even if their mouse is not moving.
    // This is particularly useful when the user is using a keyboard to manipulate an input
    const activeElement = document.activeElement
    const isInsideKeepControlsElement =
      activeElement?.closest('[data-keep-controls-while-active]') !== null

    if (!controlsVisible) setControlsVisible(true)

    if (!isInsideKeepControlsElement) {
      debouncedSetControlsVisible(false)
    } else {
      // The user is actively interacting with an element and we do not want to hide the controls
      debouncedSetControlsVisible.cancel()
    }
  }

  const { getOption } = useRundownOutputOptions()

  // `mirror` may be a comma-separated value (e.g. `vertical,horizontal`) so
  // both axes can be enabled at once — use `includes`, not equality.
  const optionMirror = getOption(OUTPUT_TYPES.MIRROR)
  const [mirrorVertically, setMirrorVertically] = useState(
    optionMirror.includes(OUTPUT_TYPES.VERTICAL),
  )
  const [mirrorHorizontally, setMirrorHorizontally] = useState(
    optionMirror.includes(OUTPUT_TYPES.HORIZONTAL),
  )

  // Remove the default body BG colour and adds the users chosen BG colour
  const optionBackgroundColor = getOption(OUTPUT_TYPES.BACKGROUND_COLOR)

  useEffect(() => {
    document.body.classList.remove('bg-black')
    document.body.style.backgroundColor = optionBackgroundColor
  }, [optionBackgroundColor])

  return (
    <main
      className={cn(
        'relative flex h-screen transition-transform max-h-svh',
        mirrorVertically && '-scale-y-100',
        mirrorHorizontally && '-scale-x-100',
        getOption(OUTPUT_TYPES.HEADER_POSITION) === OUTPUT_TYPES.TOP
          ? 'flex-col'
          : 'flex-col-reverse',
      )}
      onMouseMove={onMouseMove}
      onTouchMove={onMouseMove}
    >
      <RenderTitle />

      {/* Header */}
      <header className="shrink-0 grow-0">
        <RundownOutputHeader />
      </header>

      {/* Body */}
      <div
        className="grow overflow-y-auto overflow-x-auto"
        id="rd-scroll-container"
      >
        <RundownOutputBody controlsVisible={controlsVisible} />
      </div>

      {/* Overlay Controls */}
      {controlsVisible && (
        <div className="fixed right-3 bottom-3">
          <RundownOutputControls
            mirrorVertically={mirrorVertically}
            onMirrorVertically={() => setMirrorVertically(!mirrorVertically)}
            mirrorHorizontally={mirrorHorizontally}
            onMirrorHorizontally={() =>
              setMirrorHorizontally(!mirrorHorizontally)
            }
          />
        </div>
      )}
    </main>
  )
}

function RenderTitle() {
  const rundown = useAtomValue(rundownAtom)

  const title = `${rundown?.name} | Output` // React 19 expects a string child only for <title>

  return <title>{title}</title>
}
